import { DateTimePicker, TextFieldProps } from '@scaleaq/scaleui';
import { DateTime } from 'luxon';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import { fromApiDateTime, toApiDateTime } from '@/utils';

type ControlledDateFieldProps<T extends TextFieldProps> = {
  control: Control<T>;
  caution?: boolean | string;
  critical?: boolean | string;
  inputType?: 'text' | 'number';
  maxWidth?: string;
  name: Path<T>;
  render?: boolean;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
} & Omit<TextFieldProps, 'value' | 'onChange' | 'pattern' | 'type' | 'defaultValue'>;

const toPickerDateTime = (value: unknown): DateTime => {
  if (value instanceof DateTime) return value.toLocal();
  if (typeof value === 'string' && value) return fromApiDateTime(value);
  return DateTime.now();
};

export const ControlledDateField = <T extends FieldValues>({
  control,
  caution,
  critical,
  label,
  name,
  render = true,
  rules,
  inputType = 'text',
  ...props
}: ControlledDateFieldProps<T>) => {
  void inputType;
  if (!render) return;

  return (
    <div style={{ maxWidth: props.maxWidth, width: '100%' }}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <DateTimePicker
            onChange={next => onChange(next ? toApiDateTime(next) : '')}
            date={toPickerDateTime(value)}
            label={label}
            maxDate={DateTime.now()}
            caution={caution}
            critical={critical}
          />
        )}
      />
    </div>
  );
};
